import { useSyncExternalStore } from "react";
import { api } from "../../lib/api";

// A queue per document survives route/tab changes and serializes network writes.
// Mongo revision checking prevents one browser from silently overwriting another.
const stores = new Map();
const cacheKey = id => `se-carousel-draft-v3-${id}`;
const message = error => typeof error?.response?.data?.detail === "string"
  ? error.response.data.detail : "Enregistrement impossible. Votre brouillon reste conservé sur cet appareil.";

function createStore(initial) {
  let cached;
  try { cached = JSON.parse(localStorage.getItem(cacheKey(initial.id))); } catch { /* unavailable cache */ }
  const store = {
    doc: cached?.document || initial, revision: cached?.revision ?? initial.revision ?? 0,
    version: cached ? 1 : 0, savedVersion: 0, timer: null, pending: null,
    status: cached ? "pending" : "saved", error: "", listeners: new Set(),
  };
  store.emit = () => {
    store.snapshot = { document: store.doc, status: store.status, error: store.error };
    store.listeners.forEach(listener => listener());
  };
  store.cache = () => {
    try { localStorage.setItem(cacheKey(initial.id), JSON.stringify({ document: store.doc, revision: store.revision })); }
    catch { store.error = "Stockage local indisponible : gardez cette page ouverte jusqu’à l’enregistrement."; }
  };
  store.save = () => {
    clearTimeout(store.timer);
    if (store.pending) return store.pending;
    store.pending = (async () => {
      while (store.version !== store.savedVersion) {
        const version = store.version;
        const { format, slides, global_typography = {} } = store.doc;
        store.status = "saving"; store.error = ""; store.emit();
        try {
          const result = await api.put(`/admin/marketing/carousels/${initial.id}/document`, {
            format, slides, global_typography, revision: store.revision,
          });
          store.revision = result.data.revision;
          store.doc = { ...store.doc, revision: store.revision };
          store.savedVersion = version;
          store.cache();
        } catch (error) {
          store.status = "error"; store.error = message(error); store.emit();
          throw error;
        }
      }
      localStorage.removeItem(cacheKey(initial.id));
      store.status = "saved"; store.emit();
    })().finally(() => { store.pending = null; });
    return store.pending;
  };
  store.change = updater => {
    store.doc = updater(store.doc);
    store.version += 1; store.status = "pending"; store.error = "";
    store.cache(); store.emit(); clearTimeout(store.timer);
    store.timer = setTimeout(() => store.save().catch(() => {}), 400);
  };
  store.discard = async () => {
    clearTimeout(store.timer);
    if (store.pending) await store.pending.catch(() => {});
    const result = await api.get(`/admin/marketing/carousels/${initial.id}`);
    store.doc = result.data; store.revision = result.data.revision;
    store.version = 0; store.savedVersion = 0; store.status = "saved"; store.error = "";
    localStorage.removeItem(cacheKey(initial.id)); store.emit();
  };
  store.emit();
  if (cached) store.timer = setTimeout(() => store.save().catch(() => {}), 400);
  return store;
}

export const useCarouselDocument = initial => {
  if (!stores.has(initial.id)) stores.set(initial.id, createStore(initial));
  const store = stores.get(initial.id);
  const state = useSyncExternalStore(listener => {
    store.listeners.add(listener);
    return () => store.listeners.delete(listener);
  }, () => store.snapshot);
  return { ...state, change: store.change, save: store.save, discard: store.discard };
};